import type { AsyncThunk, UnknownAction } from '@reduxjs/toolkit';
import { configureStore } from '@reduxjs/toolkit';
import type { ThunkAction } from 'redux-thunk';

import type { ServiceError } from '@models';
import rootReducer from '@store/reducers';

import { api } from './api';
import { handle401ErrorsMiddleware } from './middleware/handle-401-errors';

export type RootState = ReturnType<typeof rootReducer>;
export type PreloadedState = Partial<RootState>;

const buildStore = (devMode: boolean, preloadedState?: PreloadedState) =>
  // Create the main Redux store, add the reducers and any required middleware
  configureStore({
    reducer: rootReducer,
    preloadedState,
    devTools: devMode,
    middleware: getDefaultMiddleware =>
      getDefaultMiddleware({ immutableCheck: false })
        // RTK Query middleware enables caching, invalidation, polling, and other query features
        .concat(api.middleware)
        // Handle 401 errors from all sources (RTK Query, legacy actions, etc.)
        .concat(handle401ErrorsMiddleware)
        .concat(
          // eslint-disable-next-line global-require, @typescript-eslint/no-require-imports
          devMode && process.env.BROWSER ? require('redux-logger').logger : [],
        ),
  });

export type Store = ReturnType<typeof buildStore>;
export type AppDispatch = Store['dispatch'];

export type AppThunk<ReturnType = void> = ThunkAction<
  ReturnType,
  RootState,
  unknown,
  UnknownAction
>;

export type AppAsyncThunk<Return, Params> = AsyncThunk<
  Return,
  Params,
  {
    state: RootState;
    dispatch: AppDispatch;
    rejectValue: ServiceError;
  }
>;

export default buildStore;
